import { Link } from 'react-router-dom';
import {
  DollarSign,
  Receipt,
  Car,
  CalendarDays,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  FileText,
  Hash,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { useExpenses, useMyExpenses, useDashboardStats } from '@/hooks/use-payroll';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { ExpenseListItem } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';
import { MissingTimesheetsWidget } from './missing-timesheets-widget';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-purple-100 text-purple-800',
};

export function PayrollDashboardPage() {
  const orgCurrency = useOrgCurrency();
  const permissions = usePermissions();
  const canManagePayroll = permissions.hasMinModuleRole('payroll', 'manager');

  const { data: pendingExpenses } = useExpenses({ status: 'submitted' });
  const { data: myExpenses } = useMyExpenses();
  const { data: dashboardStats } = useDashboardStats(canManagePayroll);

  const counts = dashboardStats?.expense_status_counts;

  const myUnreimbursed = myExpenses?.filter(
    (e: ExpenseListItem) => e.status !== 'reimbursed' && e.status !== 'rejected' && e.status !== 'draft'
  );
  const myUnreimbursedTotal = myUnreimbursed?.reduce(
    (sum: number, e: ExpenseListItem) => sum + parseFloat(e.amount),
    0
  ) ?? 0;

  const summaryCards = [
    {
      title: 'Pending Approval',
      value: counts?.submitted ?? '-',
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      link: '/payroll/expenses?status=submitted',
    },
    {
      title: 'Approved This Month',
      value: counts?.approved ?? '-',
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50',
      link: '/payroll/expenses?status=approved',
    },
    {
      title: 'Total Reimbursed',
      value: counts?.reimbursed ?? '-',
      icon: DollarSign,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      link: '/payroll/expenses?status=reimbursed',
    },
    {
      title: 'My Unreimbursed',
      value: formatMoney(myUnreimbursedTotal, orgCurrency),
      icon: AlertCircle,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      link: '/payroll/my-expenses',
    },
  ];

  const quickActions = [
    { label: 'New Expense', icon: Plus, to: '/payroll/expenses/new' },
    { label: 'All Expenses', icon: Receipt, to: '/payroll/expenses' },
    { label: 'Mileage', icon: Car, to: '/payroll/mileage' },
    { label: 'Per Diem', icon: CalendarDays, to: '/payroll/per-diem' },
    { label: 'Cost Codes', icon: Hash, to: '/payroll/cost-codes' },
    ...(canManagePayroll
      ? [{ label: 'Reports', icon: FileText, to: '/payroll/reports' }]
      : []),
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1><img src="/logos/h3pay.svg" alt="H3Pay" className="h-7" /></h1>
          <p className="text-gray-600 mt-1">Payroll &amp; Expense Management</p>
        </div>
        <Button asChild>
          <Link to="/payroll/expenses/new">
            <Plus className="h-4 w-4 mr-2" />
            New Expense
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <Link key={card.title} to={card.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{card.title}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${card.bg}`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Missing Timesheets Widget (managers only) */}
      {canManagePayroll && <MissingTimesheetsWidget />}

      {/* Pending Approval Queue (managers only) */}
      {canManagePayroll && pendingExpenses && pendingExpenses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingExpenses.slice(0, 5).map((expense) => (
                <Link
                  key={expense.id}
                  to={`/payroll/expenses/${expense.id}`}
                  className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{expense.description}</p>
                    <p className="text-sm text-gray-600">
                      {expense.tech_name} &middot; {expense.category_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {formatMoney(expense.amount, expense.currency ?? orgCurrency)}
                    </span>
                    <Badge className={statusColors[expense.status] ?? 'bg-gray-100 text-gray-800'}>
                      {expense.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </Link>
              ))}
              {pendingExpenses.length > 5 && (
                <Link
                  to="/payroll/expenses?status=submitted"
                  className="text-sm text-blue-600 hover:underline block text-center"
                >
                  View all {pendingExpenses.length} pending expenses
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Recent Expenses */}
      {myExpenses && myExpenses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-500" />
              My Recent Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {myExpenses.slice(0, 5).map((expense) => (
                <Link
                  key={expense.id}
                  to={`/payroll/expenses/${expense.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{expense.description}</p>
                    <p className="text-sm text-gray-600">
                      {expense.date} &middot; {expense.category_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {formatMoney(expense.amount, expense.currency ?? orgCurrency)}
                    </span>
                    <Badge className={statusColors[expense.status] ?? 'bg-gray-100 text-gray-800'}>
                      {expense.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard Enhancements */}
      {canManagePayroll && dashboardStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Unbilled & Upcoming Payroll */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Unbilled Expenses</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatMoney(dashboardStats.unbilled_expenses, orgCurrency)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Billable but not invoiced</p>
                  </div>
                  <div className="p-3 rounded-lg bg-orange-50">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Upcoming Payroll</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatMoney(dashboardStats.upcoming_payroll, orgCurrency)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Estimated from timesheets</p>
                  </div>
                  <div className="p-3 rounded-lg bg-indigo-50">
                    <TrendingUp className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Spend Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-500" />
                Monthly Spend Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardStats.spend_trend.length > 0 ? (
                <div className="space-y-2">
                  {dashboardStats.spend_trend.map((item) => {
                    const itemTotal = parseFloat(item.expenses) + parseFloat(item.payroll);
                    const maxAmount = Math.max(
                      ...dashboardStats.spend_trend.map((s) => parseFloat(s.expenses) + parseFloat(s.payroll))
                    );
                    const pct = maxAmount > 0 ? (itemTotal / maxAmount) * 100 : 0;
                    return (
                      <div key={item.month} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-20 shrink-0">{item.month}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-blue-500 h-full rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium w-20 text-right">
                          {formatMoney(itemTotal, orgCurrency)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No spend data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Categories */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-green-500" />
                Top Expense Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardStats.top_categories.length > 0 ? (
                <div className="space-y-3">
                  {dashboardStats.top_categories.map((cat) => {
                    const maxCat = Math.max(
                      ...dashboardStats.top_categories.map((c) => parseFloat(c.total))
                    );
                    const pct = maxCat > 0 ? (parseFloat(cat.total) / maxCat) * 100 : 0;
                    return (
                      <div key={cat.category__name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{cat.category__name}</span>
                          <span className="font-medium">
                            {formatMoney(cat.total, orgCurrency)} ({cat.count})
                          </span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-green-500 h-full rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No expense data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
                asChild
              >
                <Link to={action.to}>
                  <action.icon className="h-5 w-5" />
                  <span className="text-sm">{action.label}</span>
                </Link>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
